import {
  Input,
  PageHeader,
  Table,
  Popover,
  Modal as AntModal,
  Row,
  Typography,
} from "antd";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Team } from "shared/interfaces/Team";
import { SearchOutlined, EyeOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import careerStages from "shared/constants/careerStages";
import { ReactComponent as ShareIcon } from "shared/assets/images/share-icon.svg";
import { ReactComponent as BellIcon } from "shared/assets/images/bell-icon.svg";
import { ReactComponent as TrashIcon } from "shared/assets/images/trash-icon.svg";
import { UserDiagnosis } from "shared/interfaces/UserDiagnosis";
import { BRLFormat } from "shared/helpers/BRLFormat";
import ModalShareDiagnosis from "./ModalShareDiagnosis";
import {
  deleteDiagnostic,
  remiderDiagnosticByEmail,
} from "shared/service/StrmService";
import { DiagnosticStatus } from "shared/enums/DiagnosticStatus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMinus } from "@fortawesome/free-solid-svg-icons";
import PanelDiagnosticButtons from "./PanelDiagnosticButtons";

const { success, confirm } = AntModal;
interface PanelProps {
  team?: Team;
  getTeams: () => void;
}

const statusInfo = [
  { text: "sent", icon: <FontAwesomeIcon icon={faCheck} /> },
  { text: "rejected", icon: <FontAwesomeIcon icon={faCheck} /> },
  { text: "available", icon: <FontAwesomeIcon icon={faCheck} /> },
  { text: "progress", icon: <FontAwesomeIcon icon={faCheck} /> },
  { text: "finished", icon: <FontAwesomeIcon icon={faCheck} /> },
  { text: "canceled", icon: <FontAwesomeIcon icon={faCheck} /> },
  { text: "expired", icon: <FontAwesomeIcon icon={faMinus} /> },
  { text: "expired", icon: <FontAwesomeIcon icon={faCheck} /> },
];

const Panel: React.FC<PanelProps> = (props) => {
  const { team, getTeams } = props;
  const { t } = useTranslation("radar");
  const [diagnosticFilter, setDiagnosticFilter] = useState<string>("");
  const [showModalShare, setShowModalShare] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserDiagnosis>();
  const [filteredDiagnosis, setFilteredDiagnosis] = useState<UserDiagnosis[]>(
    []
  );

  const formatDate = (date: string) => {
    const momentDate = moment(date);
    return momentDate.isValid() ? momentDate.format("DD/MM/YYYY") : "-";
  };

  const fomatDateTime = (date: string) => {
    const momentDate = moment(date);
    return momentDate.isValid() ? momentDate.format("DD/MM/YYYY - HH:mm") : "-";
  };

  const onClickOpenUrl = (user: UserDiagnosis) => {
    const shareCondition: any = shareUrlAndStatusFinished(user);
    if (shareCondition) window.open(user.diagResulUrl, "_blank");
  };

  const shareUrlAndStatusFinished = ({
    status,
    diagResulUrl,
  }: UserDiagnosis) => {
    return (status === DiagnosticStatus.Finished && diagResulUrl) || false;
  };

  const onClickShare = (user: UserDiagnosis) => {
    const shareCondition: any = shareUrlAndStatusFinished(user);
    if (shareCondition) {
      setSelectedUser(user);
      setShowModalShare(true);
    }
  };

  const onShareDiagnosis = () => {
    setShowModalShare(false);
  };

  const onClickNotifyByEmail = async (userDiagnosis: UserDiagnosis) => {
    const canceled =
      userDiagnosis?.status !== DiagnosticStatus.CancelledByInvestor &&
      userDiagnosis?.status !== DiagnosticStatus.CancelledByInviteExpired &&
      userDiagnosis?.status !== DiagnosticStatus.CancelledByNotFinishedInTime;

    if (!userDiagnosis?.diagResulUrl && userDiagnosis.id && canceled) {
      const response = await remiderDiagnosticByEmail(userDiagnosis.id);
      if (response)
        success({
          title: t("pages.Diagnostic.modalDiagnoseSuccessTitle"),
          content: t("pages.Diagnostic.notifyByEmailSuccess"),
          icon: "",
          okButtonProps: {
            className: "botao-verde",
            style: { padding: 0 },
          },
          okType: "link",
          onOk() {
            console.log("ok");
          },
        });
    }
  };

  const deleteDiagnosis = async (userDiagnosisId?: string) => {
    if (userDiagnosisId) {
      const response = await deleteDiagnostic(userDiagnosisId);
      if (response) {
        success({
          title: t("pages.Diagnostic.modalDiagnoseSuccessTitle"),
          content: t("pages.Diagnostic.modalDeleteDiagnosisContent"),
          icon: "",
          okButtonProps: {
            className: "botao-verde",
            style: { padding: 0 },
          },
          okType: "link",
          onOk() {
            getTeams();
          },
        });
      }
    }
  };

  const onClickDeleteDiagnosis = async (userDiagnosis: UserDiagnosis) => {
    const sent = userDiagnosis?.status === DiagnosticStatus.InviteSent;
    if (sent && userDiagnosis.id) {
      confirm({
        title: t("pages.Diagnostic.alert"),
        className: "alert",
        content: t("pages.Diagnostic.modalDeleteDiagnosis"),
        icon: "",
        okText: t("pages.Diagnostic.yes"),
        okButtonProps: {
          className: "botao-verde",
          style: { padding: 0 },
        },
        okType: "link",
        cancelText: t("pages.Diagnostic.no"),
        cancelButtonProps: {
          className: "botao-vermelho",
          style: { padding: 0 },
        },
        onOk() {
          deleteDiagnosis(userDiagnosis.id);
        },
      });
    }
  };

  const actionStyle = (enabled: boolean) =>
    enabled
      ? {
          opacity: 1,
          cursor: "pointer",
        }
      : {
          opacity: 0.25,
          cursor: "not-allowed",
        };

  const userColumns: ColumnsType<UserDiagnosis> = [
    {
      title: t("pages.Diagnostic.name"),
      dataIndex: "name",
      align: "left",
    },
    {
      title: t("pages.Diagnostic.email"),
      dataIndex: "userEmail",
      align: "left",
    },
    {
      title: t("pages.Diagnostic.status"),
      dataIndex: "status",
      align: "left",
      render: (value: number) => {
        const info = statusInfo[value];
        return (
          <span className={`status ${info.text}`}>
            {info.icon} {t(`pages.Diagnostic.${info.text}`)}
          </span>
        );
      },
    },
    {
      title: t("pages.Diagnostic.stage"),
      dataIndex: "stage",
      align: "left",
      render: (stage: number) =>
        (stage && t(`pages.Radar.${careerStages[stage - 1]}`)) || "-",
    },
    {
      title: t("pages.Diagnostic.access"),
      dataIndex: "artistLastChangeOn",
      align: "right",
      render: (value: any) => formatDate(value),
    },
    {
      title: t("pages.Diagnostic.dispatch"),
      dataIndex: "invitedOn",
      align: "right",
      render: (value: any) => formatDate(value),
    },
    {
      title: t("pages.Diagnostic.actions"),
      align: "center",
      render: (_: any, record: UserDiagnosis) => {
        const shareCondition: any = shareUrlAndStatusFinished(record);
        const canceled =
          record?.status !== DiagnosticStatus.CancelledByInvestor &&
          record?.status !== DiagnosticStatus.CancelledByInviteExpired &&
          record?.status !== DiagnosticStatus.CancelledByNotFinishedInTime;
        const sent = record?.status === DiagnosticStatus.InviteSent;

        return (
          <div
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Popover showArrow={false} content={t("pages.Diagnostic.delete")}>
              <TrashIcon
                style={{
                  ...actionStyle(sent),
                }}
                onClick={() => onClickDeleteDiagnosis(record)}
              />
            </Popover>
            <Popover showArrow={false} content={t("pages.Diagnostic.share")}>
              <ShareIcon
                style={{
                  ...actionStyle(shareCondition),
                }}
                onClick={() => onClickShare(record)}
              />
            </Popover>
            <Popover
              showArrow={false}
              content={t("pages.Diagnostic.visualize")}
            >
              <span
                onClick={() => onClickOpenUrl(record)}
                style={{ ...actionStyle(shareCondition) }}
              >
                <EyeOutlined />
              </span>
            </Popover>
            <Popover showArrow={false} content={t("pages.Diagnostic.notify")}>
              <BellIcon
                style={{
                  ...actionStyle(!record?.diagResulUrl && canceled),
                }}
                onClick={() => onClickNotifyByEmail(record)}
              />
            </Popover>
          </div>
        );
      },
    },
  ];

  const transactionsColumns: ColumnsType<UserDiagnosis> = [
    {
      title: t("pages.Diagnostic.quantity"),
      dataIndex: "quantity",
      align: "left",
      render: (value: number) => String(value).padStart(2, "0") || 0,
    },
    {
      title: t("pages.Diagnostic.purchaseOn"),
      dataIndex: "purchaseOn",
      align: "right",
      render: (value: any) => fomatDateTime(value),
    },
    {
      title: t("pages.Diagnostic.amount"),
      dataIndex: "amount",
      align: "right",
      render: (value: number) => BRLFormat(value),
    },
  ];

  useEffect(() => {
    const filtered = diagnosticFilter
      ? team?.userDiagnosis?.filter(({ name }) =>
          name?.toUpperCase().includes(diagnosticFilter.toUpperCase())
        ) || []
      : team?.userDiagnosis || [];
    setFilteredDiagnosis(filtered);
  }, [team, diagnosticFilter]);

  return (
    <>
      <Row>
        <h1 className="title-strm">{t("pages.Diagnostic.titleStrm")}</h1>
      </Row>
      <Row>
        <Typography.Paragraph className="explanation">
          {t("pages.Diagnostic.explanation")}
        </Typography.Paragraph>
      </Row>
      <PanelDiagnosticButtons teamId={team?.id} getTeams={getTeams} />

      <div className="caixa-credito-convite">
        <span>{t("pages.Diagnostic.credits")}</span>
        <b style={{ color: "#E6E6E6" }}>{team?.diagnosisRemainingCredits}</b>
      </div>
      <PageHeader
        title={t("pages.Diagnostic.panelTitle")}
        extra={[
          <Input
            onChange={debounce(
              (e) => setDiagnosticFilter(e.target.value),
              1000
            )}
            addonAfter={<SearchOutlined />}
          />,
        ]}
      />
      <Table
        columns={userColumns}
        dataSource={filteredDiagnosis}
        pagination={{
          showSizeChanger: false,
          showQuickJumper: false,
          showPrevNextJumpers: false,
        }}
      />
      <PageHeader
        title={t("pages.Diagnostic.transactionsTitle")}
        subTitle={t("pages.Diagnostic.transactionsSubTitle")}
      />
      <Table
        columns={transactionsColumns}
        dataSource={team?.paymentTransactions}
        className="table-read-only"
        tableLayout="fixed"
        pagination={{
          showSizeChanger: false,
          hideOnSinglePage: true,
          showQuickJumper: false,
          showPrevNextJumpers: false,
        }}
      />
      <ModalShareDiagnosis
        visible={showModalShare}
        onCancel={() => setShowModalShare(false)}
        onSend={onShareDiagnosis}
        diagnosis={selectedUser}
      />
    </>
  );
};

export default Panel;
